import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../state";
import { Line } from "react-chartjs-2";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { isEqual } from "lodash";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

// Set global font defaults
ChartJS.defaults.font.family = "JaguarExuberant, Helvetica, Arial, sans-serif";

function EnergyDeltaGraph({ carColour }: { carColour: string }) {
  const raceData = useSelector((state: RootState) => state.raceData, isEqual);

  const jagDrivers = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.jagDrivers,
    isEqual,
  );
  const driverInfo = useSelector(
    (state: RootState) => state.imports.derivedImportData.strategy.driverInfo,
    isEqual,
  );

  const isPitBoostRace = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.BPitBoostRace === 1,
  );

  const variablePitBoostEnabled = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.BEnableVariablePitBoost === 1,
  );

  const EPitBoostVsScenario = useSelector(
    (state: RootState) =>
      state.imports.imports.strategy?.data.EPitBoostVsScenario,
  );

  const chartData = useMemo(() => {
    const referenceTable = raceData[carColour]?.lapTable ?? {};

    // Get all laps where reference has energy (excluding lap 0)
    const referenceLaps = Object.entries(referenceTable)
      .filter(
        ([lapNum, lap]) =>
          lap && lap.energy !== null && parseInt(lapNum, 10) !== 0,
      )
      .map(([lapNum, lap]) => ({
        lap: parseInt(lapNum, 10),
        energy: lap.energy as number,
      }));

    // Build reference energy map
    const referenceEnergyMap: Record<number, number> = {};
    for (const { lap, energy } of referenceLaps) {
      referenceEnergyMap[lap] = energy;
    }

    // All lap numbers from reference
    const lapNumbers = referenceLaps.map((l) => l.lap).sort((a, b) => a - b);

    const datasets = Object.entries(jagDrivers).map(
      ([driverCarColour, driverNumber]) => {
        const driver = driverNumber ? driverInfo[driverNumber] : undefined;
        const driverName = driver?.driverName;
        const driverColour = driver?.driverHighlightColourAlt || "#888";

        const driverTable = raceData[driverCarColour]?.lapTable;
        const driverRaceConfig = raceData[driverCarColour]?.raceConfig;
        const driverPitLap = driverRaceConfig?.pitLap ?? null;
        const dataPoints: { x: number; y: number }[] = [];

        if (driverTable) {
          for (const lapNum of lapNumbers) {
            const driverLap = driverTable[lapNum];
            const refEnergy = referenceEnergyMap[lapNum];

            if (
              driverLap &&
              driverLap.energy !== null &&
              refEnergy !== undefined
            ) {
              let carEnergy = driverLap.energy;

              const referenceScenario =
                referenceTable[lapNum]?.pitScenario ?? null;
              const driverScenario = driverLap.pitScenario ?? null;
              if (
                isPitBoostRace &&
                variablePitBoostEnabled &&
                referenceScenario !== null &&
                driverScenario != null &&
                (driverPitLap === null || lapNum <= driverPitLap)
              ) {
                const referenceScenarioEnergy =
                  EPitBoostVsScenario?.[referenceScenario - 1] ?? 0;
                const driverScenarioEnergy =
                  EPitBoostVsScenario?.[driverScenario - 1] ?? 0;
                carEnergy += referenceScenarioEnergy - driverScenarioEnergy;
              }

              const delta = carEnergy - refEnergy;
              dataPoints.push({ x: lapNum, y: delta });
            }
          }
        }

        return {
          label: driverName,
          data: dataPoints,
          backgroundColor: driverColour,
          borderColor: driverColour,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: false,
        };
      },
    );

    return {
      labels: lapNumbers,
      datasets: [...datasets],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    raceData,
    carColour,
    jagDrivers,
    driverInfo,
    isPitBoostRace,
    EPitBoostVsScenario,
  ]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    clip: false as const,
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        display: true,
        position: "top" as const,
        labels: {
          color: "#ffffff",
          usePointStyle: true,
          pointStyle: "line" as const,
        },
      },
      annotation: {
        annotations: {
          zeroLine: {
            type: "line" as const,
            yMin: 0,
            yMax: 0,
            borderColor: "#888888",
            borderDash: [6, 6],
            borderWidth: 1,
          },
        },
      },
      title: {
        display: true,
        text: "Energy Delta",
        color: "#ffffff",
        font: {
          size: 16,
          weight: "bold" as const,
        },
      },
    },
    scales: {
      x: {
        type: "linear" as const,
        title: {
          display: true,
          text: "Lap",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
          stepSize: 1,
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
        min: 0,
        max: raceData[carColour]?.raceConfig?.currentLap || 1,
      },
      y: {
        title: {
          display: true,
          text: "Energy Delta (kWh)",
          color: "#ffffff",
        },
        ticks: {
          color: "#ffffff",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
        border: {
          color: "#ffffff",
        },
        min: -2,
        max: 2,
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default EnergyDeltaGraph;
